import { useFlag } from '@calendly/feature-check-react'
import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { useMeetingTypesPath } from 'hooks/use_meeting_types_path'

import {
  isSettingsModalNavigationActive,
  mergeSettingsSearch,
} from './constants'

/**
 * Resolves a Link / history target: query-style on the current pathname when the modal flow is active,
 * otherwise the legacy path string.
 * @param {{
 *   section: string,
 *   legacyPath: string,
 *   location: { pathname: string, search: string, hash: string },
 *   settingsTab?: string | null,
 *   isRebrandSettingsIaEnabled: boolean,
 * }} args
 * @returns {string | { pathname: string, search: string, hash: string }}
 */
export function resolveSettingsNavigationTarget({
  section,
  legacyPath,
  location,
  settingsTab,
  isRebrandSettingsIaEnabled,
}) {
  if (isSettingsModalNavigationActive({ isRebrandSettingsIaEnabled, search: location.search })) {
    return {
      pathname: location.pathname,
      search: mergeSettingsSearch(section, location.search, settingsTab),
      hash: location.hash,
    }
  }
  return legacyPath
}

/**
 * Client-side settings navigation: use query params when the settings modal flow is active
 * (`ap_rebrand_settings_ia` + `?settings=`), otherwise legacy routes.
 */
export function useSettingsNavigation() {
  const location = useLocation()
  const history = useHistory()
  const meetingTypesPath = useMeetingTypesPath()
  const isRebrandSettingsIaEnabled = useFlag('ap_rebrand_settings_ia', false)

  const isSettingsModalNavigationActiveValue = useMemo(
    () => isSettingsModalNavigationActive({
      isRebrandSettingsIaEnabled,
      search: location.search,
    }),
    [isRebrandSettingsIaEnabled, location.search],
  )

  const hrefForSettingsSection = useCallback(
    (section, legacyPath, settingsTab) => resolveSettingsNavigationTarget({
      section,
      legacyPath,
      location,
      settingsTab,
      isRebrandSettingsIaEnabled,
    }),
    [location, isRebrandSettingsIaEnabled],
  )

  const navigateToSettingsSection = useCallback(
    (section, { legacyPath, settingsTab, replace = false } = {}) => {
      const target = resolveSettingsNavigationTarget({
        section,
        legacyPath,
        location,
        settingsTab,
        isRebrandSettingsIaEnabled,
      })
      if (replace) {
        history.replace(target)
      } else {
        history.push(target)
      }
    },
    [history, location, isRebrandSettingsIaEnabled],
  )

  const legacyPersonalUrlRedirectTo = useCallback(
    (section, settingsTab) => ({
      pathname: meetingTypesPath,
      search: mergeSettingsSearch(section, location.search, settingsTab),
      hash: location.hash,
    }),
    [meetingTypesPath, location.hash, location.search],
  )

  return {
    isSettingsModalNavigationActive: isSettingsModalNavigationActiveValue,
    hrefForSettingsSection,
    legacyPersonalUrlRedirectTo,
    navigateToSettingsSection,
  }
}
